import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Box, AppBar, Toolbar, Typography, Button, Container } from "@mui/material";
import { Outlet, useNavigate } from "react-router-dom";
import { getSalesOrderFilterOptions } from "./services/api";

// Shared context so SalesOrderList can consume pre-fetched filter options
// without triggering a second network request.
export const SoraFilterOptionsContext = createContext({ data: null, loading: false });

export const useSoraFilterOptions = () => useContext(SoraFilterOptionsContext);

const NavigationBar = () => {
	const navigate = useNavigate();

	return (
		<AppBar
			position="sticky"
			elevation={0}
			sx={{
				backgroundColor: "rgba(255, 255, 255, 0.8)",
				backdropFilter: "blur(20px)",
				borderBottom: "1px solid",
				borderColor: "divider",
				color: "text.primary",
			}}>
			<Toolbar
				sx={{
					maxWidth: "1200px",
					width: "100%",
					mx: "auto",
					px: { xs: 2, sm: 3 },
				}}>
				<Typography
					variant="h6"
					sx={{
						flexGrow: 1,
						cursor: "pointer",
						fontWeight: 600,
						fontSize: "1.25rem",
						letterSpacing: "-0.01em",
					}}
					onClick={() => navigate("/explore/sora/")}>
					SORA
				</Typography>

				<Button
					onClick={() => navigate("/explore/sora/")}
					sx={{
						color: "text.primary",
						textTransform: "none",
						fontSize: "0.9375rem",
						fontWeight: 400,
						px: 2,
						"&:hover": { backgroundColor: "transparent", opacity: 0.7 },
					}}>
					Dashboard
				</Button>

				<Button
					onClick={() => navigate("/explore/sora/sales-orders")}
					sx={{
						color: "text.primary",
						textTransform: "none",
						fontSize: "0.9375rem",
						fontWeight: 400,
						px: 2,
						"&:hover": { backgroundColor: "transparent", opacity: 0.7 },
					}}>
					Sales Orders
				</Button>

				<Button
					onClick={() => navigate("/explore/sora/return-orders")}
					sx={{
						color: "text.primary",
						textTransform: "none",
						fontSize: "0.9375rem",
						fontWeight: 400,
						px: 2,
						"&:hover": { backgroundColor: "transparent", opacity: 0.7 },
					}}>
					Return Orders
				</Button>
			</Toolbar>
		</AppBar>
	);
};

const AppContent = () => {
	const [filterOptionsData, setFilterOptionsData] = useState(null);
	const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
	const fetchedRef = useRef(false);

	// Prefetch filter options as soon as Sora mounts — before the user navigates
	// to Sales Orders — so dropdowns are instant when they arrive.
	useEffect(() => {
		if (fetchedRef.current) return;
		fetchedRef.current = true;

		const prefetch = async () => {
			setFilterOptionsLoading(true);
			try {
				const response = await getSalesOrderFilterOptions();
				setFilterOptionsData(response.data);
			} catch (error) {
				console.error("Failed to prefetch sales order filter options:", error);
			} finally {
				setFilterOptionsLoading(false);
			}
		};

		prefetch();
	}, []);

	return (
		<SoraFilterOptionsContext.Provider value={{ data: filterOptionsData, loading: filterOptionsLoading }}>
			<Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
				<NavigationBar />

				<Box
					component="main"
					sx={{
						flexGrow: 1,
						py: { xs: 4, sm: 6, md: 8 },
						backgroundColor: "background.default",
					}}>
					<Outlet />
				</Box>

				<Box
					component="footer"
					sx={{
						py: 4,
						px: 2,
						mt: "auto",
						backgroundColor: "background.default",
						borderTop: "1px solid",
						borderColor: "divider",
					}}>
					<Container maxWidth="lg">
						<Typography variant="body2" color="text.secondary" align="center" sx={{ fontSize: "0.8125rem" }}>
							© {new Date().getFullYear()} SORA
						</Typography>
					</Container>
				</Box>
			</Box>
		</SoraFilterOptionsContext.Provider>
	);
};

const Sora = () => {
	return <AppContent />;
};

export default Sora;
